import React from 'react';
import styled from 'styled-components';
import { MdBrush } from 'react-icons/md';
import { FaPalette, FaCrown } from 'react-icons/fa';

const AboutContainer = styled.section`
  padding: 5rem 2rem 4rem;
  background: 
    radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.01) 0%, transparent 50%),
    radial-gradient(circle at 70% 80%, rgba(212, 175, 55, 0.008) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.006) 0%, transparent 60%),
    linear-gradient(135deg, var(--color-obsidian) 0%, var(--color-charcoal) 25%, var(--color-obsidian) 50%, var(--color-charcoal) 75%, var(--color-obsidian) 100%);
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.008) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.006) 0%, transparent 60%),
      radial-gradient(circle at 50% 10%, rgba(212, 175, 55, 0.004) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    animation: luxury-ambient-glow 30s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 800px;
    height: 800px;
    background: radial-gradient(circle at center, rgba(212, 175, 55, 0.004) 0%, rgba(212, 175, 55, 0.001) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(10rem);
    pointer-events: none;
    z-index: 1;
    animation: luxury-pulse 20s ease-in-out infinite;
  }
  
  @media (max-width: 968px) {
    padding: 4rem 1.5rem 3rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1rem 2.5rem;
    min-height: 60vh;
    
    &::after {
      width: 600px;
      height: 600px;
    }
  }
  
  @media (max-width: 576px) {
    padding: 2.5rem 0.75rem 2rem;
    min-height: 50vh;
    
    &::after {
      width: 400px;
      height: 400px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 2rem 0.5rem 1.75rem;
    min-height: 45vh;
    
    &::after {
      width: 300px;
      height: 300px;
    }
  }
  
  @media (max-width: 375px) {
    padding: 1.75rem 0.375rem 1.5rem;
    min-height: 40vh;
    
    &::after {
      width: 250px;
      height: 250px;
    }
  }
  
  @media (max-width: 320px) {
    padding: 1.5rem 0.25rem 1.25rem;
    min-height: 35vh;
    
    &::after {
      width: 200px;
      height: 200px;
    }
  }
`;

const AboutContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 1200px) {
    gap: 5rem;
    max-width: 1200px;
  }
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 4rem;
    text-align: center;
    max-width: 800px;
  }
  
  @media (max-width: 768px) {
    gap: 3rem;
    max-width: 90%;
  }
  
  @media (max-width: 576px) {
    gap: 2.5rem;
    max-width: 95%;
  }
  
  @media (max-width: 480px) {
    gap: 2rem;
    max-width: 100%;
  }
  
  @media (max-width: 375px) {
    gap: 1.75rem;
    padding: 0 1rem;
  }
  
  @media (max-width: 320px) {
    gap: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const TextContent = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2rem;
    left: -2rem;
    right: -2rem;
    bottom: -2rem;
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.004) 0%, transparent 70%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.003) 0%, transparent 70%);
    border-radius: 20px;
    filter: blur(1rem);
    opacity: 0.8;
    z-index: -1;
  }
  
  @media (max-width: 968px) {
    &::before {
      display: none;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 1.5rem;
  color: var(--color-pearl);
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.1;
  position: relative;
  font-family: 'Inter', sans-serif;
  text-shadow: 
    0 4px 8px rgba(11, 11, 11, 0.9),
    0 0 40px rgba(212, 175, 55, 0.12),
    0 0 80px rgba(212, 175, 55, 0.06);
  animation: luxury-glow 8s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.003) 0%, transparent 70%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.002) 0%, transparent 70%);
    filter: blur(2rem);
    z-index: -1;
    animation: luxury-text-aura 12s ease-in-out infinite;
  }
  
  span {
    background: linear-gradient(135deg, #D4AF37 0%, #E8B86D 20%, #F8F8F8 40%, #E8B86D 60%, #D4AF37 80%, #E8B86D 100%);
    background-size: 400% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: luxury-text-shimmer 8s ease-in-out infinite;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, transparent, var(--color-champagne), var(--color-rose-gold), var(--color-champagne), transparent);
      opacity: 0.8;
      border-radius: 2px;
      animation: luxury-underline-pulse 6s ease-in-out infinite;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1.5rem;
    left: 0;
    width: 120px;
    height: 3px;
    background: linear-gradient(90deg, var(--color-champagne), var(--color-rose-gold), transparent);
    opacity: 0.8;
    border-radius: 2px;
    animation: luxury-divider-pulse 4s ease-in-out infinite;
  }
  
  @media (max-width: 968px) {
    margin-bottom: 2.5rem;
    text-align: center;
    
    &::after {
      left: 50%;
      transform: translateX(-50%);
      width: 100px;
    }
    
    span::after {
      height: 3px;
      bottom: -10px;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    
    &::after {
      width: 80px;
      height: 2px;
    }
  }
`;

const MainText = styled.p`
  font-size: clamp(1rem, 2vw, 1.125rem);
  color: var(--color-slate-300);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-weight: 400;
  opacity: 0.95;
  letter-spacing: 0.012em;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.5);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: -16px;
    right: -16px;
    bottom: -8px;
    background: rgba(212, 175, 55, 0.002);
    border-radius: 8px;
    filter: blur(0.5rem);
    z-index: -1;
    opacity: 0.6;
  }
  
  @media (max-width: 968px) {
    margin-bottom: 2.5rem;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    line-height: 1.7;
  }
`;

const SecondaryText = styled.p`
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: var(--color-slate-400);
  line-height: 1.5;
  margin-bottom: 2rem;
  font-weight: 400;
  opacity: 0.92;
  letter-spacing: 0.008em;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.5);
  
  @media (max-width: 968px) {
    margin-bottom: 3.5rem;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    line-height: 1.6;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, rgba(22, 22, 22, 0.75) 0%, rgba(11, 11, 11, 0.85) 100%);
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: 20px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(12px);
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -150%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(212, 175, 55, 0.05),
      rgba(212, 175, 55, 0.15),
      rgba(212, 175, 55, 0.05),
      transparent
    );
    transform: skewX(-25deg);
    transition: 0.75s;
  }
  
  &:hover {
    transform: translateY(-6px);
    border-color: rgba(212, 175, 55, 0.35);
    box-shadow: 
      0 20px 40px rgba(212, 175, 55, 0.15),
      0 0 30px rgba(212, 175, 55, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
      
    &::before {
      left: 150%;
    }
  }
`;

const StatIconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(212, 175, 55, 0.08);
  border: 1px solid rgba(212, 175, 55, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: var(--color-champagne);
  font-size: 1.35rem;
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  
  ${StatCard}:hover & {
    background: var(--color-champagne);
    color: var(--color-obsidian);
    transform: scale(1.1) rotate(8deg);
    box-shadow: 0 0 15px rgba(212, 175, 55, 0.3);
  }
`;

const StatNumber = styled.div`
  font-size: clamp(2.2rem, 3vw, 2.8rem);
  font-weight: 900;
  margin-bottom: 0.35rem;
  letter-spacing: -0.02em;
  line-height: 1;
  background: linear-gradient(135deg, var(--color-platinum) 0%, var(--color-champagne) 50%, var(--color-rose-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
`;

const StatLabel = styled.div`
  font-size: clamp(0.75rem, 1vw, 0.85rem);
  color: var(--color-slate-400);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  text-align: center;
  transition: all 0.4s ease;
  
  ${StatCard}:hover & {
    color: var(--color-pearl);
  }
`;

const VisualContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CollageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 968px) {
    height: 420px;
    margin-top: 2rem;
  }
  @media (max-width: 480px) {
    height: 340px;
  }
`;

const CollageCard = styled.div<{ $index: number }>`
  position: absolute;
  width: 260px;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(212, 175, 55, 0.18);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(212, 175, 55, 0.05);
  background: var(--color-charcoal);
  transition: all 0.6s cubic-bezier(0.25, 1, 0.5, 1);
  cursor: pointer;
  z-index: ${props => 3 - props.$index};

  /* Initial positions to create an overlapping layered fan effect */
  ${props => {
    if (props.$index === 0) {
      return `
        transform: translate(-90px, -20px) rotate(-8deg);
      `;
    } else if (props.$index === 1) {
      return `
        transform: translate(0px, 10px) rotate(2deg);
        z-index: 4;
      `;
    } else {
      return `
        transform: translate(90px, -10px) rotate(8deg);
      `;
    }
  }}

  &:hover {
    transform: translate(${props => props.$index === 0 ? '-100px' : props.$index === 1 ? '0px' : '100px'}, -30px) scale(1.12) rotate(0deg);
    z-index: 10;
    border-color: var(--color-champagne);
    box-shadow: 0 25px 50px rgba(212, 175, 55, 0.35), 0 0 30px rgba(212, 175, 55, 0.15);
    
    & > .card-overlay {
      opacity: 1;
      background: linear-gradient(to top, rgba(11, 11, 11, 0.98) 0%, rgba(11, 11, 11, 0.6) 70%, transparent 100%);
    }
  }

  @media (max-width: 768px) {
    width: 200px;
    height: 270px;
    ${props => {
      if (props.$index === 0) {
        return `transform: translate(-65px, -15px) rotate(-6deg);`;
      } else if (props.$index === 1) {
        return `transform: translate(0px, 8px) rotate(1deg);`;
      } else {
        return `transform: translate(65px, -8px) rotate(6deg);`;
      }
    }}
    
    &:hover {
      transform: translate(${props => props.$index === 0 ? '-75px' : props.$index === 1 ? '0px' : '75px'}, -20px) scale(1.1) rotate(0deg);
    }
  }

  @media (max-width: 480px) {
    width: 150px;
    height: 200px;
    ${props => {
      if (props.$index === 0) {
        return `transform: translate(-50px, -10px) rotate(-5deg);`;
      } else if (props.$index === 1) {
        return `transform: translate(0px, 5px) rotate(1deg);`;
      } else {
        return `transform: translate(50px, -5px) rotate(5deg);`;
      }
    }}
    
    &:hover {
      transform: translate(${props => props.$index === 0 ? '-55px' : props.$index === 1 ? '0px' : '55px'}, -15px) scale(1.08) rotate(0deg);
    }
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;
  
  ${CollageCard}:hover & {
    transform: scale(1.08);
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(11, 11, 11, 0.95) 0%, rgba(11, 11, 11, 0.4) 50%, transparent 100%);
  padding: 1.5rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  opacity: 0.85;
  transition: all 0.4s ease;
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem;
  }
`;

const CardLabel = styled.span`
  font-size: 0.7rem;
  color: var(--color-champagne);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 700;
  margin-bottom: 0.25rem;
  
  @media (max-width: 480px) {
    font-size: 0.55rem;
    letter-spacing: 0.1em;
  }
`;

const CardTitle = styled.h4`
  font-size: 0.95rem;
  color: var(--color-pearl);
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.01em;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;



const About: React.FC = () => {
  return (
    <AboutContainer id="about">
      <AboutContent>
        <TextContent>
          <SectionTitle>CRAFTING <span>LEGENDS</span></SectionTitle>
          <MainText>
            At KIII Toys Studios, we breathe life into legendary characters through our premium action figure collection. 
            Our passion lies in celebrating the heroes that have shaped our culture - from iconic Indian cinema legends 
            to global superheroes.
          </MainText>
          <SecondaryText>
            Each figure is a masterpiece, meticulously handcrafted with premium materials and attention to detail that 
            honors the legacy of these incredible characters. Our limited editions become treasured collector's items, 
            showcasing the pinnacle of artistic craftsmanship.
          </SecondaryText>
          
          <StatsContainer>
            <StatCard>
              <StatIconContainer>
                {React.createElement(FaPalette as React.ComponentType<any>)}
              </StatIconContainer>
              <StatNumber>50+</StatNumber>
              <StatLabel>Characters Created</StatLabel>
            </StatCard>
            <StatCard>
              <StatIconContainer>
                {React.createElement(FaCrown as React.ComponentType<any>)}
              </StatIconContainer>
              <StatNumber>1000+</StatNumber>
              <StatLabel>Happy Collectors</StatLabel>
            </StatCard>
          </StatsContainer>
        </TextContent>
        
        <VisualContent>
          <CollageContainer>
            <CollageCard $index={0}>
              <CardImage src="/work-images/thanos-1.jpg" alt="Thanos Crafting" />
              <CardOverlay className="card-overlay">
                <CardLabel>Precision</CardLabel>
                <CardTitle>Sculpting</CardTitle>
              </CardOverlay>
            </CollageCard>
            <CollageCard $index={1}>
              <CardImage src="/work-images/groot-1.jpg" alt="Groot Painting" />
              <CardOverlay className="card-overlay">
                <CardLabel>Artistry</CardLabel>
                <CardTitle>Hand-Painting</CardTitle>
              </CardOverlay>
            </CollageCard>
            <CollageCard $index={2}>
              <CardImage src="/work-images/loki-1.jpg" alt="Loki Finish" />
              <CardOverlay className="card-overlay">
                <CardLabel>Masterpiece</CardLabel>
                <CardTitle>Exquisite Finish</CardTitle>
              </CardOverlay>
            </CollageCard>
          </CollageContainer>
        </VisualContent>
      </AboutContent>
    </AboutContainer>
  );
};

export default About;